import * as React from 'react'

import { LinkIcon } from 'lucide-react'

import type { IAttachment } from '~/client/graph'
import { Button } from '~/client/src/shared/components/Button/Button'
import {
  DynamicFieldRenderer,
  useDynamicForm,
} from '~/client/src/shared/components/DynamicForm'
import type { FileUploadFn } from '~/client/src/shared/components/DynamicForm/types'
import { useResourceFields } from '~/client/src/shared/components/ResourceViewEdit/useResourceFields'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type { ResourcePropertyInput } from '~/client/src/shared/resources/resourcePropertyInput'
import { classifyResourcePropertyInput } from '~/client/src/shared/resources/resourcePropertyInput'
import type ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import formatBytes from '~/client/src/shared/utils/formatBytes'

import type { ResourceProperty } from '../ResourceProperties/types'

interface IProps {
  property: ResourceProperty | null
  resourceTypeId?: string
  projectDateStore?: ProjectDateStore
  onConfigure: () => void
}

const UnavailablePreview: React.FC<{
  message: string
  onConfigure: () => void
}> = ({ message, onConfigure }) => (
  <div className="tw:flex tw:flex-col tw:gap-3 tw:px-4 tw:py-6">
    <div className="tw:text-title-sm tw:text-foreground">
      {Localization.translator.propertyPreviewUnavailable}
    </div>
    <div className="tw:text-body-sm tw:text-muted-foreground">{message}</div>
    <div>
      <Button type="button" variant="outline" onClick={onConfigure}>
        {Localization.translator.backToDetails}
      </Button>
    </div>
  </div>
)

const ManagedRelationPreview: React.FC<{
  property: ResourceProperty
  linkedResourceLabel: string
}> = ({ property, linkedResourceLabel }) => (
  <div className="tw:flex tw:flex-col tw:items-start tw:gap-3 tw:px-4 tw:py-4">
    <div className="tw:text-label-lg tw:text-foreground">
      {property.displayName || property.name}
    </div>
    <Button type="button" variant="outline" size="lg" disabled>
      <LinkIcon data-icon="inline-start" size={16} />
      {Localization.translator.linkToX(linkedResourceLabel)}
    </Button>
    <div className="tw:text-body-sm tw:text-muted-foreground">
      {Localization.translator.managedRelationPreviewHelp}
    </div>
  </div>
)

const CalculatedValuePreview: React.FC<{
  property: ResourceProperty
}> = ({ property }) => {
  const unit = property.configuration?.unit

  return (
    <div className="tw:flex tw:flex-col tw:gap-2 tw:px-4 tw:py-4">
      <div className="tw:text-label-lg tw:text-foreground">
        {property.displayName || property.name}
      </div>
      <div className="tw:flex tw:min-h-10 tw:items-center tw:rounded-md tw:border tw:border-solid tw:border-input tw:bg-muted tw:px-3 tw:text-body-md tw:text-muted-foreground">
        —{unit ? ` ${unit}` : ''}
      </div>
      <div className="tw:text-body-sm tw:text-muted-foreground">
        {Localization.translator.calculatedPropertyPreviewHelp}
      </div>
    </div>
  )
}

const assertNever = (value: never): never => {
  throw new Error(`Unhandled resource property input: ${String(value)}`)
}

const PropertyPreviewPanel: React.FC<IProps> = ({
  property,
  resourceTypeId,
  projectDateStore,
  onConfigure,
}) => {
  const fileUrls = React.useRef(new Set<string>())
  const propertyInput: ResourcePropertyInput = property
    ? classifyResourcePropertyInput(property)
    : { kind: 'unsupported' }
  const propertyDefinitions = React.useMemo(
    () =>
      property && propertyInput.kind === 'editable-field' ? [property] : [],
    [property, propertyInput.kind],
  )

  React.useEffect(
    () => () => {
      for (const url of fileUrls.current) {
        URL.revokeObjectURL(url)
      }
      fileUrls.current.clear()
    },
    [],
  )

  const uploadPreviewFile = React.useCallback<FileUploadFn>((file: File) => {
    const url = URL.createObjectURL(file)
    fileUrls.current.add(url)

    const attachment: IAttachment = {
      fileName: file.name,
      size: formatBytes(file.size),
      url,
    }
    return Promise.resolve(attachment)
  }, [])

  const { fields, updateField } = useResourceFields({
    record: null,
    propertyDefinitions,
    recordId: 'new',
    resourceTypeId,
    projectDateStore,
    fieldOptions: {
      includeStaticFields: false,
      allOptional: true,
      includeAllProperties: true,
      isPreview: true,
    },
  })

  const previewField = fields[0] ?? null
  const formMethods = useDynamicForm(previewField ? [previewField] : [])

  if (!property) {
    return (
      <UnavailablePreview
        message={Localization.translator.configurePropertyBeforePreview}
        onConfigure={onConfigure}
      />
    )
  }

  switch (propertyInput.kind) {
    case 'editable-field':
      return previewField ? (
        <div className="tw:px-4 tw:py-4">
          <DynamicFieldRenderer
            field={previewField}
            control={formMethods.control}
            fileUploadFn={uploadPreviewFile}
            onFieldChange={updateField}
          />
        </div>
      ) : (
        <UnavailablePreview
          message={Localization.translator.unsupportedPropertyPreviewHelp}
          onConfigure={onConfigure}
        />
      )
    case 'managed-relation':
      return (
        <ManagedRelationPreview
          property={property}
          linkedResourceLabel={propertyInput.definition.target.label}
        />
      )
    case 'calculated-value':
      return <CalculatedValuePreview property={property} />
    case 'unsupported':
      return (
        <UnavailablePreview
          message={Localization.translator.unsupportedPropertyPreviewHelp}
          onConfigure={onConfigure}
        />
      )
    default:
      return assertNever(propertyInput)
  }
}

export default PropertyPreviewPanel
